import React, { useEffect, useState } from "react";
import * as Lucide from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BRAND } from "@/content/landing";
import { INOCHI as STATIC_INOCHI } from "@/content/inochi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

const SHOWCASE_SLUG = "inochi";

const InochiCtx = React.createContext(STATIC_INOCHI);
const useInochi = () => React.useContext(InochiCtx);

// Merge live DB profile with static fallback content.
// Live fields override static. Uploaded media replaces gallery/videos when present.
function mergeWithStatic(live) {
  if (!live) return STATIC_INOCHI;
  const merged = { ...STATIC_INOCHI, ...live };
  if (live.overview) merged.overview = { ...STATIC_INOCHI.overview, ...live.overview };
  const photos = live?.media?.photos || [];
  if (photos.length > 0) {
    merged.productGallery = photos.map((p) => ({
      src: p.url?.startsWith("http") ? p.url : `${BACKEND}${p.url}`,
      alt: p.title || p.filename || "Product photo",
    }));
  }
  const videos = live?.media?.videos || [];
  if (videos.length > 0) {
    merged.videos = videos.map((v) => ({
      key: v.media_id,
      title: v.title || v.filename,
      thumb: STATIC_INOCHI.overview.heroImage,
      url: v.url?.startsWith("http") ? v.url : `${BACKEND}${v.url}`,
    }));
  }
  return merged;
}

const startLogin = (route) => {
  const redirect = window.location.origin + (route || "/dashboard");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

const trackEvent = (event) => {
  axios.post(`${BACKEND}/api/metrics/event`, { event }).catch(() => {});
};

function Icon({ name, ...props }) {
  const C = Lucide[name] || Lucide.Sparkles;
  return <C {...props} />;
}

// ============================================================
// HEADER
// ============================================================
function Header({ navigate }) {
  const INOCHI = useInochi();
  return (
    <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="showcase-brand-link">
          <img src={BRAND.logo} alt={BRAND.name} className="w-10 h-10 object-contain" />
          <div className="leading-tight text-left">
            <div className="font-display font-black text-base tracking-tight">{BRAND.name}</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">{BRAND.tagline}</div>
          </div>
        </button>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-zinc-700">
          <a href="#overview"    className="hover:text-[#C8102E]">Overview</a>
          <a href="#opportunity" className="hover:text-[#C8102E]">Opportunities</a>
          <a href="#income"      className="hover:text-[#C8102E]">Income</a>
          <a href="#faq"         className="hover:text-[#C8102E]">FAQ</a>
        </nav>
        <button
          onClick={() => { trackEvent("inochi_header_apply"); startLogin(INOCHI.applyRoute); }}
          data-testid="showcase-header-apply"
          className="px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
          style={{ background: RED }}
        >
          Apply now
        </button>
      </div>
    </header>
  );
}

// ============================================================
// 1. OVERVIEW
// ============================================================
function Overview() {
  const INOCHI = useInochi();
  const o = INOCHI.overview;
  return (
    <section id="overview" className="relative overflow-hidden border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
      <div className="absolute inset-0 opacity-25">
        <img src={o.heroImage} alt={o.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${CHARCOAL}EE 0%, ${CHARCOAL}AA 60%, transparent 100%)` }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 border border-white/30" data-testid="overview-tag">
          <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Featured vendor showcase
        </div>
        <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-6" data-testid="overview-name">
          {o.name}
        </h1>
        <div className="font-display text-xl sm:text-2xl mt-2" style={{ color: GOLD }} data-testid="overview-tagline">
          {o.tagline}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-white/60 font-semibold">{o.category} · Origin: {o.origin}</div>
        <p className="mt-7 text-white/85 text-base sm:text-lg max-w-3xl">{o.intro}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => { trackEvent("inochi_hero_affiliate"); startLogin(INOCHI.applyRoute); }}
            data-testid="overview-cta-affiliate"
            className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
            style={{ background: RED }}
          >
            Apply as Affiliate <Lucide.ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => { trackEvent("inochi_hero_agent"); startLogin(INOCHI.applyRoute); }}
            data-testid="overview-cta-agent"
            className="inline-flex items-center gap-2 px-6 py-3 font-semibold"
            style={{ background: GOLD, color: CHARCOAL }}
          >
            Apply as Agent <Lucide.ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Brand story + market
// ============================================================
function StoryAndMarket() {
  const INOCHI = useInochi();
  const o = INOCHI.overview;
  return (
    <section className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Brand story</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-4 tracking-tight">The story behind INOCHI.</h2>
          <p className="mt-5 text-zinc-700 leading-relaxed">{o.brandStory}</p>
          <div className="tag mt-10"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Market opportunity</div>
          <p className="mt-4 text-zinc-700 leading-relaxed">{o.marketOpportunity}</p>
        </div>
        <div className="lg:col-span-5">
          <div className="bg-white border-2 p-7" style={{ borderColor: RED }}>
            <div className="overline" style={{ color: RED }}>Why INOCHI</div>
            <ul className="mt-4 space-y-3" data-testid="overview-highlights">
              {o.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <Lucide.CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: RED }} />
                  <span className="text-sm text-zinc-700">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 2. OPPORTUNITY TYPES
// ============================================================
function Opportunities() {
  const INOCHI = useInochi();
  return (
    <section id="opportunity" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Opportunities</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Two ways to earn with INOCHI.</h2>
          </div>
          <p className="max-w-md text-zinc-600">
            Start with zero capital as an affiliate, or unlock higher margins and exclusive territory as an agent.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="opportunity-types-grid">
          {INOCHI.opportunities.map((o) => (
            <div
              key={o.key}
              data-testid={`opp-type-${o.key}`}
              className={`relative p-7 bg-white border-2 transition-all hover:shadow-[8px_8px_0_0_#C8102E] ${o.featured ? "lg:-translate-y-1" : ""}`}
              style={{ borderColor: o.featured ? GOLD : "#E4E4E7" }}
            >
              {o.featured && (
                <div className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-widest px-3 py-1" style={{ background: GOLD, color: CHARCOAL }}>
                  Most Popular
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 grid place-items-center text-white" style={{ background: RED }}>
                  <Icon name={o.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-display font-black text-2xl tracking-tight">{o.title}</h3>
                  <div className="text-xs uppercase tracking-widest font-semibold mt-1" style={{ color: RED }}>
                    Capital: {o.capital}
                  </div>
                </div>
              </div>
              <p className="mt-5 text-zinc-700">{o.summary}</p>
              <ul className="mt-4 space-y-2">
                {o.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Lucide.Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: RED }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { trackEvent("inochi_opp_" + o.key); startLogin(INOCHI.applyRoute); }}
                data-testid={`opp-cta-${o.key}`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 text-white py-3 font-semibold hover:opacity-90 transition-opacity"
                style={{ background: RED }}
              >
                {o.ctaLabel} <Lucide.ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 3. PRODUCT GALLERY
// ============================================================
function Gallery() {
  const INOCHI = useInochi();
  return (
    <section className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Products</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">The INOCHI product line.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="product-gallery">
          {INOCHI.productGallery.map((p, i) => (
            <div key={p.src} data-testid={`product-photo-${i}`} className="relative overflow-hidden bg-zinc-100" style={{ aspectRatio: "1/1" }}>
              <img src={p.src} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 4. VIDEOS (TikTok-style horizontal scroll)
// ============================================================
function Videos() {
  const INOCHI = useInochi();
  return (
    <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Promo videos</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Watch & learn.</h2>
          </div>
          <p className="max-w-md text-zinc-600">
            Product introductions, demonstrations and founder updates.
          </p>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-3 -mx-5 px-5 snap-x snap-mandatory" data-testid="video-strip">
          {INOCHI.videos.map((v) => (
            <div
              key={v.key}
              data-testid={`video-${v.key}`}
              className="shrink-0 snap-start w-[60vw] sm:w-72 relative overflow-hidden bg-zinc-900 group cursor-pointer"
              style={{ aspectRatio: "9/16" }}
            >
              <img src={v.thumb} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-14 h-14 grid place-items-center rounded-full bg-white/90 shadow-lg">
                  <Lucide.Play className="w-6 h-6" style={{ color: RED }} fill={RED} />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.8), transparent)" }}>
                <div className="font-display font-black text-sm tracking-tight">{v.title}</div>
                {!v.url && (
                  <div className="text-[9px] uppercase tracking-widest text-white/70 mt-0.5">Coming soon</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 5. TESTIMONIALS
// ============================================================
function Testimonials() {
  const INOCHI = useInochi();
  return (
    <section className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Trust</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Real results, real people.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="testimonials-grid">
          {INOCHI.testimonials.map((t) => (
            <div key={t.key} data-testid={`testimonial-${t.key}`} className="bg-white border border-zinc-200 p-6">
              <Lucide.Quote className="w-7 h-7" style={{ color: RED }} />
              <p className="mt-4 text-zinc-700">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-5 flex items-center gap-3">
                <img src={t.photo} alt={t.author} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                <div className="leading-tight">
                  <div className="font-semibold text-sm">{t.author}</div>
                  <div className="text-xs text-zinc-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 6. TRAINING & SUPPORT
// ============================================================
function Training() {
  const INOCHI = useInochi();
  return (
    <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="mb-10">
          <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Training & support</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">You won&apos;t be selling alone.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="training-grid">
          {INOCHI.training.map((t) => (
            <div key={t.title} className="bg-white border border-zinc-200 p-6 hover:border-[#C8102E] transition-all">
              <div className="w-12 h-12 grid place-items-center text-white" style={{ background: CHARCOAL }}>
                <Icon name={t.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg mt-4 tracking-tight">{t.title}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-snug">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 7. INCOME OPPORTUNITY
// ============================================================
function Income() {
  const INOCHI = useInochi();
  return (
    <section id="income" className="border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="tag border-white/20" style={{ color: "white" }}>
              <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Income
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">{INOCHI.income.headline}</h2>
            <p className="mt-5 text-white/80">{INOCHI.income.body}</p>
            <button
              onClick={() => { trackEvent("inochi_income_apply"); startLogin(INOCHI.applyRoute); }}
              data-testid="income-cta"
              className="mt-7 inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
              style={{ background: RED }}
            >
              Start Earning <Lucide.ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-white text-zinc-900" data-testid="income-tiers">
              {INOCHI.income.tiers.map((t, i) => (
                <div key={t.tier} className={`p-5 grid grid-cols-12 gap-4 items-center ${i < INOCHI.income.tiers.length - 1 ? "border-b border-zinc-200" : ""}`}>
                  <div className="col-span-12 sm:col-span-5">
                    <div className="font-display font-black tracking-tight">{t.tier}</div>
                  </div>
                  <div className="col-span-5 sm:col-span-3 font-display font-black text-2xl" style={{ color: RED }}>{t.commission}</div>
                  <div className="col-span-7 sm:col-span-4 text-xs text-zinc-600">{t.conditions}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 8. SUCCESS METRICS
// ============================================================
function Metrics() {
  const INOCHI = useInochi();
  return (
    <section className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-grid">
          {INOCHI.metrics.map((m) => (
            <div key={m.key} data-testid={`metric-${m.key}`} className="border border-zinc-200 p-6 text-center">
              <div className="font-display font-black text-4xl tracking-tight" style={{ color: RED }}>{m.value}</div>
              <div className="text-xs uppercase tracking-widest font-semibold text-zinc-600 mt-2">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 9. FAQ
// ============================================================
function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-zinc-200" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full py-5 flex items-center justify-between gap-4 text-left"
      >
        <span className="font-display font-black text-base sm:text-lg tracking-tight">{q}</span>
        <Lucide.ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: RED }} />
      </button>
      {open && <p className="pb-5 text-zinc-600 text-sm sm:text-base">{a}</p>}
    </div>
  );
}

function FAQ() {
  const INOCHI = useInochi();
  return (
    <section id="faq" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="text-center mb-10">
          <div className="tag justify-center inline-flex"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> FAQ</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Frequently asked.</h2>
        </div>
        <div data-testid="faq-list">
          {INOCHI.faq.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />)}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 10. FINAL APPLY CTA
// ============================================================
function ApplyCTA() {
  const INOCHI = useInochi();
  return (
    <section className="relative overflow-hidden" style={{ background: RED, color: "white" }}>
      <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 80% 50%, ${GOLD} 0%, transparent 60%)` }} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 text-center">
        <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight" data-testid="apply-cta-heading">
          Apply with INOCHI today.
        </h2>
        <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
          Choose your path — start with zero capital as an affiliate, or claim your territory as an agent.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => { trackEvent("inochi_final_affiliate"); startLogin(INOCHI.applyRoute); }}
            data-testid="apply-cta-affiliate"
            className="inline-flex items-center gap-2 bg-white px-6 py-3 font-semibold hover:bg-zinc-100 transition-colors"
            style={{ color: RED }}
          >
            Apply as Affiliate <Lucide.ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => { trackEvent("inochi_final_agent"); startLogin(INOCHI.applyRoute); }}
            data-testid="apply-cta-agent"
            className="inline-flex items-center gap-2 px-6 py-3 font-semibold"
            style={{ background: GOLD, color: CHARCOAL }}
          >
            Apply as Agent <Lucide.ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function ShowcaseInochi() {
  const navigate = useNavigate();
  const [data, setData] = useState(STATIC_INOCHI);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/vendor-profiles/${SHOWCASE_SLUG}`)
      .then((res) => { if (!cancelled) setData(mergeWithStatic(res.data)); })
      .catch(() => { /* no live profile yet — fall back to static content */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <InochiCtx.Provider value={data}>
      <div className="min-h-screen bg-white text-[#09090b]">
        <Header navigate={navigate} />
        <Overview />
        <Metrics />
        <StoryAndMarket />
        <Opportunities />
        <Gallery />
        <Videos />
        <Testimonials />
        <Income />
        <Training />
        <FAQ />
        <ApplyCTA />

        <footer className="border-t border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
            <button onClick={() => navigate("/")} className="hover:text-[#C8102E]">← Back to homepage</button>
            <span>© {new Date().getFullYear()} {BRAND.name}. INOCHI is a partner brand.</span>
          </div>
        </footer>
      </div>
    </InochiCtx.Provider>
  );
}
